import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import MfaDialog from "@/components/MfaDialog";
import { getDeviceFingerprint } from "@/components/MfaDialog";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({ user: null, loading: true });

export const useAuth = () => useContext(AuthContext);

function isDeviceTrusted(userId: string): boolean {
  try {
    const trusted = JSON.parse(localStorage.getItem("uc_trusted_devices") || "[]");
    const fp = getDeviceFingerprint();
    // Trusted for 30 days
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return trusted.some(
      (d: { userId: string; fingerprint: string; trustedAt: number }) =>
        d.userId === userId && d.fingerprint === fp && Date.now() - d.trustedAt < thirtyDays
    );
  } catch {
    return false;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaUserId, setMfaUserId] = useState("");
  const [mfaMaskedEmail, setMfaMaskedEmail] = useState("");
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const mfaPendingRef = useRef(false);

  const checkMfa = useCallback(async (sessionUser: User) => {
    // Check if user has MFA enabled on their profile
    const { data: profileData } = await supabase
      .from("profiles")
      .select("mfa_enabled")
      .eq("id", sessionUser.id)
      .maybeSingle();

    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: sessionUser.id,
      _role: "admin",
    });

    const userMfaEnabled = profileData?.mfa_enabled === true;
    const trustedDevice = isDeviceTrusted(sessionUser.id);

    if (!userMfaEnabled || (trustedDevice && !isAdmin)) {
      setUser(sessionUser);
      setLoading(false);
      return;
    }

    // MFA required — hold user in pending state
    const email = sessionUser.email || "";
    const masked = email.replace(/(.{2})(.*)(@.*)/, "$1***$3");
    setPendingUser(sessionUser);
    setMfaUserId(sessionUser.id);
    setMfaMaskedEmail(masked);
    setMfaPending(true);
    mfaPendingRef.current = true;
    setLoading(false);
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          if (_event === "SIGNED_IN") {
            checkMfa(session.user);
          } else {
            // For token refresh etc, don't re-trigger MFA
            if (!mfaPendingRef.current) {
              setUser(session.user);
            }
          }
        } else {
          setUser(null);
          setPendingUser(null);
          setMfaPending(false);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        // On page load, check if device is trusted (skip MFA dialog for trusted devices)
        if (isDeviceTrusted(session.user.id)) {
          setUser(session.user);
          setLoading(false);
        } else {
          checkMfa(session.user);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkMfa]);

  const handleMfaVerified = () => {
    setMfaPending(false);
    mfaPendingRef.current = false;
    setUser(pendingUser);
    setPendingUser(null);
  };

  const handleMfaCancel = async () => {
    setMfaPending(false);
    mfaPendingRef.current = false;
    setPendingUser(null);
    setUser(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
      <MfaDialog
        open={mfaPending}
        userId={mfaUserId}
        maskedEmail={mfaMaskedEmail}
        onVerified={handleMfaVerified}
        onCancel={handleMfaCancel}
      />
    </AuthContext.Provider>
  );
};
